import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { OverlayContainer, useOverlay, usePreventScroll } from '@react-aria/overlays';
import { FocusScope } from '@react-aria/focus';

import type { CSSProperties, RefObject } from 'react';

import { WeekSelectorButton } from './components/WeekSelectorButton';
import { WeekSelectorOptionContainer } from './components/WeekSelectorOptionContainer';
import { WeekSelectorOption } from './components/WeekSelectorOption';
import styles from './WeekSelector.module.css';

import { Backdrop } from 'components/Backdrop';
import { useOnClickOutside } from 'components/hooks/useOnClickOutside';

export type PeriodWeek = {
  academicWeekNumber: number;
  calendarWeekNumber: number;
  isBlocked: boolean;
  period: number;
  startDate: string;
  endDate: string;
  weekNumber: number;
};

export interface WeekSelectorProps {
  /** Object with periodnumbers with array of weeks in period */
  weeks: Record<string, PeriodWeek[]>;
  /** Current period to disable previous periods */
  currentPeriod?: number;
  /** Default selected week */
  defaultSelection?: number;
  /** Prefix for the week button label */
  weekPrefix: string;
  /** Labels for each period */
  periodLabels: string[];
  /** Header text for the options container */
  optionsHeader: string;
  /** Callback function when a week is selected */
  onSelectWeek: (weeknumber: number) => void;
  /** Ref for the dropdown portal target */
  portalContainerRef?: RefObject<HTMLElement | null>;
  /** Optional WeekSelectorOptionContainer offset values [top, bottom] */
  optionContainerOffset?: number[];
}

export const WeekSelector = ({
  weeks,
  currentPeriod = 0,
  defaultSelection,
  weekPrefix,
  optionsHeader,
  periodLabels,
  onSelectWeek,
  portalContainerRef,
  optionContainerOffset = [],
}: WeekSelectorProps) => {
  const [selectedOption, setSelectedOption] = useState<number | null>(null);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const optionContainerRef = useRef<HTMLDivElement>(null);
  const periodKeys = useMemo(() => Object.keys(weeks ?? {}), [weeks]);

  useEffect(() => {
    if (defaultSelection) {
      for (const period in weeks) {
        if (
          weeks[period].some((week) => week.calendarWeekNumber === defaultSelection && !week.isBlocked) &&
          Number(period) > currentPeriod
        ) {
          setSelectedOption(defaultSelection);
          return;
        }
      }
    }
    for (const period in weeks) {
      const firstAvailableWeek = weeks[period].find((week) => !week.isBlocked);
      if (firstAvailableWeek && Number(period) > currentPeriod) {
        setSelectedOption(firstAvailableWeek.calendarWeekNumber);
        return;
      }
    }
  }, [weeks, currentPeriod, defaultSelection]);

  useEffect(() => {
    if (isOpen) {
      requestAnimationFrame(() => {
        const selectedButton = optionContainerRef.current?.querySelector<HTMLButtonElement>(
          `.${styles['week-selector-option-week--selected']}`
        );
        if (selectedButton) {
          selectedButton.focus();
        } else {
          optionContainerRef.current?.querySelector<HTMLButtonElement>('button:not(:disabled)')?.focus();
        }
      });
    }
  }, [isOpen, selectedOption]);

  const getButtonLabel = () => {
    const weekNumber = selectedOption ?? defaultSelection ?? '?';
    return `${weekPrefix} ${weekNumber}`;
  };

  useOnClickOutside(wrapperRef as RefObject<HTMLElement>, (event) => {
    if (optionContainerRef.current && optionContainerRef.current.contains(event.target as Node)) {
      return;
    }
    setIsOpen(false);
  });

  const { overlayProps, underlayProps } = useOverlay(
    {
      isOpen,
      isDismissable: true,
      onClose: () => setIsOpen(false),
    },
    optionContainerRef
  );

  usePreventScroll({ isDisabled: !isOpen || !portalContainerRef?.current });

  const OptionList = (
    <WeekSelectorOptionContainer
      header={optionsHeader}
      periodLabels={periodLabels}
      ref={optionContainerRef}
      // eslint-disable-next-line @typescript-eslint/no-magic-numbers
      {...(optionContainerOffset.length === 2
        ? {
            style: {
              top: optionContainerOffset[0],
              left: optionContainerOffset[1],
            } as CSSProperties,
          }
        : {})}
    >
      {periodKeys.map((periodIndex) => (
        <WeekSelectorOption
          key={periodIndex}
          periodIndex={periodIndex}
          weeks={weeks}
          disabled={Number(periodIndex) <= currentPeriod}
          selectedOption={selectedOption}
          setSelectedOption={setSelectedOption}
          onSelectWeek={onSelectWeek}
          setIsOpen={setIsOpen}
        />
      ))}
    </WeekSelectorOptionContainer>
  );

  return (
    <div className={styles['week-selector']} tabIndex={-1} ref={wrapperRef}>
      <WeekSelectorButton label={getButtonLabel()} onClick={() => setIsOpen(!isOpen)} />

      {isOpen &&
        (portalContainerRef?.current
          ? createPortal(
              <OverlayContainer>
                <Backdrop {...underlayProps}>
                  <FocusScope contain restoreFocus>
                    <div {...overlayProps}>{OptionList}</div>
                  </FocusScope>
                </Backdrop>
              </OverlayContainer>,
              portalContainerRef.current
            )
          : OptionList)}
    </div>
  );
};
